<template>
  <th>
    <div
      :style="styleWidth"
      class="justify-content-between align-items-start"
      :class="{ 'd-flex': !fullscreen }"
    >
      <a
        href="#"
        class="fw-semibold"
        @click="sortFn(columnId)"
        v-if="!modalstack"
      >
        <slot />
        <i
          v-if="listModel.order_field === columnId"
          :class="{
            bi: true,
            'bi-arrow-up': listModel.order_direction == 'DESC',
            'bi-arrow-down': listModel.order_direction == 'ASC',
          }"
        />
      </a>
      <slot v-else />
      <a
        href="#"
        class="fw-semibold"
        @click="toggleFilter"
        v-if="!isHomePage && !modalstack"
      >
        <i class="bi bi-filter" />
      </a>
    </div>
    <div
      v-if="isFilterOpen"
      class="filter-dropdown d-block mt-2"
    >
      <div
        v-if="options.length > 0"
        class="d-flex flex-row mb-3"
      >
        <MultiSelectDropdown
          v-model="selectedOptions"
          :options="options"
          :button-width="buttonWidth"
          @changeTdStyle="handleChangeTdStyle"
        />
      </div>
      <div
        v-else-if="props.columnId.includes('date') || props.columnId.includes('till')"
        class="d-flex flex-row mb-3"
      >
        <input
          type="date"
          class="form-control me-2"
          v-model="startDate"
          placeholder="Начальная дата"
        />
        <input
          type="date"
          class="form-control"
          v-model="endDate"
          placeholder="Конечная дата"
        />
      </div>
      <div
        v-else
        class="d-flex flex-row mb-3"
        :style="styleWidth"
      >
        <input
          class="form-control"
          placeholder="Фильтр"
          v-model="searchTerm"
        />
      </div>
    </div>
  </th>
</template>
<script setup>
import { computed, inject, onMounted, ref, shallowRef, watch } from 'vue'
import { watchThrottled } from '@vueuse/core'
import MultiSelectDropdown from '@/components/dropdowns/MultiSelectDropdown.vue'
import { storeToRefs } from 'pinia'
import { useUiStore } from '@provise/stores/ui'

const { fullscreen } = storeToRefs(useUiStore())

const isHomePage = ref(false)
onMounted(() => {
  isHomePage.value = document.location.pathname === '/'
})

var options = ref([])

const searchTerm = shallowRef('')
const isFilterOpen = ref(false)
const startDate = ref('')
const endDate = ref('')
const selectedOptions = ref([])
var filter_term = ''

const emit = defineEmits(['changeTdStyle'])

const handleChangeTdStyle = isOpen => {
  emit('changeTdStyle', isOpen)
}

const toggleFilter = () => {
  if (isFilterOpen.value) {
    // on CloseFilter Event
    searchTerm.value = ''
    startDate.value = ''
    endDate.value = ''
    selectedOptions.value = []
    handleChangeTdStyle(false)
    if (filter_term) {
      modelVSet({ page: 1, filter: props.columnId, filter_id: '' })
    }
  }
  isFilterOpen.value = !isFilterOpen.value
}

const formatDate = dateString => {
  const [year, month, day] = dateString.split('-')
  return `${day}.${month}.${year}`
}

const isValidDate = dateString => {
  const datePattern = /^\d{2}\.\d{2}\.\d{4}$/
  if (!datePattern.test(dateString)) {
    return false
  }
  const [day, month, year] = dateString.split('.').map(Number)
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

watch([startDate, endDate], ([newStartDate, newEndDate]) => {
  if (newStartDate && newEndDate) {
    const fStartDate = formatDate(newStartDate)
    const fEndDate = formatDate(newEndDate)
    if (isValidDate(fStartDate) && isValidDate(fEndDate)) {
      const filterValue = `${fStartDate}-${fEndDate}`
      filter_term = filterValue
      modelVSet({ page: 1, filter: props.columnId, filter_id: filterValue })
    }
  }
})

watch(selectedOptions, newSelectedOptions => {
  let filter_field = props.columnId
  const selectedLabels = options.value
    .filter(option => newSelectedOptions.includes(option.value))
    .map(option => option.label)
    .join('[..]')
  if (props.cfg?.[`io_${props.columnId}`]) {
    filter_term = options.value
      .filter(option => newSelectedOptions.includes(option.value))
      .map(option => option.value)
      .join('[..]')
    const nodes = props.columnId.split('.')
    if (nodes.length > 1 && nodes[1] !== 'id') {
      filter_field = nodes[0]
    }
  } else if (filter_field.includes('activity_subtype.parent_name')) {
    filter_term = options.value
      .filter(option => newSelectedOptions.includes(option.value))
      .map(option => option.value)
      .join('[..]')
    filter_field = filter_field.replace('parent_name', 'parent_type')
  } else {
    filter_term = selectedLabels
  }
  modelVSet({ page: 1, filter: filter_field, filter_id: filter_term })
})

onMounted(() => {
  if (props.getOptions) {
    options.value = props.getOptions()
    return
  }

  if (!props.cfg) {
    return
  }

  const nodes = props.columnId.split('.')
  for (const node of nodes) {
    const cf = props.cfg[node]

    if (!Array.isArray(cf)) {
      continue
    }

    options.value = props.cfg[node].map(item => ({
      value: String(item.id),
      label: item.value ? String(item.value) : `ID: ${item.id}`,
    }))
  }
})

watchThrottled(
  searchTerm,
  (filter_id, osearch) => {
    if (filter_id === undefined && osearch === '') {
      return
    }
    filter_term = filter_id
    modelVSet({ page: 1, filter: props.columnId, filter_id })
  },
  { throttle: 500 }
)

/*

Sortable version of table header

*/
const props = defineProps(['columnId', 'width', 'cfg', 'modalstack', 'getOptions'])
const sortFn = inject('sort-fn')
const listModel = inject('list-model')
const modelVSet = inject('model-valueset')
const styleWidth = computed(() => (props.width ? { width: `${props.width}px` } : null))
const buttonWidth = computed(() => (parseInt(props.width, 10) > 130 ? props.width : '130px'))
</script>
